import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { rtlMirrorHorizontalChevron } from '@/lib/locale-direction';
import { useBreadcrumbOverrides } from '@/lib/breadcrumb-overrides';

const SEGMENT_LABELS: Record<string, string> = {
  dashboard: 'breadcrumb.dashboard',
  projects: 'breadcrumb.projects',
  eduflow: 'breadcrumb.eduflow',
  learners: 'breadcrumb.learners',
  analytics: 'breadcrumb.analytics',
  videos: 'breadcrumb.videos',
  sounds: 'breadcrumb.sounds',
  images: 'breadcrumb.images',
  documents: 'breadcrumb.documents',
  store: 'breadcrumb.store',
  help: 'breadcrumb.help',
  admin: 'breadcrumb.admin',
  settings: 'breadcrumb.settings',
  profile: 'breadcrumb.profile',
  groups: 'breadcrumb.groups',
  branding: 'breadcrumb.branding',
  'landing-page': 'breadcrumb.landingPage',
  webhooks: 'breadcrumb.webhooks',
  campaigns: 'breadcrumb.campaigns',
  competencies: 'breadcrumb.competencies',
  compliance: 'breadcrumb.compliance',
  reports: 'breadcrumb.reports',
  budget: 'breadcrumb.budget',
  catalog: 'breadcrumb.catalog',
  integrations: 'breadcrumb.integrations',
  requests: 'breadcrumb.requests',
  team: 'breadcrumb.team',
  reviews: 'breadcrumb.reviews',
  training: 'breadcrumb.training',
  skills: 'breadcrumb.skills',
  certificates: 'breadcrumb.certificates',
  new: 'breadcrumb.new',
  edit: 'breadcrumb.edit',
  podcasts: 'breadcrumb.podcasts',
  episodes: 'breadcrumb.episodes',
  'producer-assignments': 'breadcrumb.producerAssignments',
};

function humanize(segment: string): string {
  return segment
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

type Crumb = { path: string; label: string; isLast: boolean };

function buildSimpleFormationCrumbs(
  pathname: string,
  overrides: Map<string, string>,
  t: (key: string, fallback: string) => string,
): Crumb[] | null {
  const structureMatch = pathname.match(/^\/eduflow\/simple\/([^/]+)\/structure\/?$/);
  if (structureMatch) {
    const flowId = structureMatch[1];
    const flowPath = `/eduflow/simple/${flowId}`;
    const structurePath = pathname.replace(/\/$/, '') || `/eduflow/simple/${flowId}/structure`;
    return [
      { path: '/eduflow', label: t('breadcrumb.eduflow', 'Formations'), isLast: false },
      {
        path: flowPath,
        label: overrides.get(flowPath) ?? t('breadcrumb.simpleFormation', 'Formation'),
        isLast: false,
      },
      {
        path: structurePath,
        label: overrides.get(structurePath) ?? t('breadcrumb.simpleStructure', 'Structure'),
        isLast: true,
      },
    ];
  }

  const chapterMatch = pathname.match(/^\/eduflow\/simple\/([^/]+)\/chapter\/([^/]+)\/?$/);
  if (chapterMatch) {
    const flowId = chapterMatch[1];
    const chapterId = chapterMatch[2];
    const flowPath = `/eduflow/simple/${flowId}`;
    const chapterPath = `/eduflow/simple/${flowId}/chapter/${chapterId}`;
    return [
      { path: '/eduflow', label: t('breadcrumb.eduflow', 'Formations'), isLast: false },
      {
        path: `${flowPath}/structure`,
        label: overrides.get(flowPath) ?? t('breadcrumb.simpleFormation', 'Formation'),
        isLast: false,
      },
      {
        path: chapterPath,
        label: overrides.get(chapterPath) ?? t('breadcrumb.simpleChapter', 'Chapitre'),
        isLast: true,
      },
    ];
  }

  return null;
}

function buildSimplePodcastCrumbs(
  pathname: string,
  overrides: Map<string, string>,
  t: (key: string, fallback: string) => string,
): Crumb[] | null {
  const episodeMatch = pathname.match(/^\/podcasts\/simple\/([^/]+)\/episodes\/([^/]+)\/?$/);
  if (episodeMatch) {
    const podcastId = episodeMatch[1];
    const episodeId = episodeMatch[2];
    const podcastPath = `/podcasts/simple/${podcastId}`;
    const episodePath = `/podcasts/simple/${podcastId}/episodes/${episodeId}`;
    return [
      { path: '/podcasts', label: t('breadcrumb.podcasts', 'Podcasts'), isLast: false },
      {
        path: podcastPath,
        label: overrides.get(podcastPath) ?? t('breadcrumb.simplePodcast', 'Podcast'),
        isLast: false,
      },
      {
        path: episodePath,
        label: overrides.get(episodePath) ?? t('breadcrumb.simpleEpisode', 'Épisode'),
        isLast: true,
      },
    ];
  }

  const podcastMatch = pathname.match(/^\/podcasts\/simple\/([^/]+)\/?$/);
  if (podcastMatch) {
    const podcastId = podcastMatch[1];
    const podcastPath = `/podcasts/simple/${podcastId}`;
    return [
      { path: '/podcasts', label: t('breadcrumb.podcasts', 'Podcasts'), isLast: false },
      {
        path: podcastPath,
        label: overrides.get(podcastPath) ?? t('breadcrumb.simplePodcast', 'Podcast'),
        isLast: true,
      },
    ];
  }

  return null;
}

export function Breadcrumb() {
  const { t } = useTranslation();
  const location = useLocation();
  const overrides = useBreadcrumbOverrides();

  const segments = location.pathname.split('/').filter(Boolean);

  if (segments.length === 0) return null;

  const simpleCrumbs =
    buildSimpleFormationCrumbs(location.pathname, overrides, t) ??
    buildSimplePodcastCrumbs(location.pathname, overrides, t);
  const crumbs: Crumb[] = simpleCrumbs ?? segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/');
    const override = overrides.get(path);
    const labelKey = SEGMENT_LABELS[segment];
    const label = override ?? (labelKey ? t(labelKey, humanize(segment)) : humanize(segment));
    const isLast = index === segments.length - 1;
    return { path, label, isLast };
  });

  return (
    <nav aria-label={t('breadcrumb.label', 'Breadcrumb')} className="flex items-center">
      <ol className="flex items-center gap-1 text-sm">
        <li>
          <Link
            to="/dashboard"
            className="flex items-center text-[#5C5C5C]/70 dark:text-[#E8D5BC] hover:text-[#5C5C5C] dark:hover:text-[#5C5C5C]/50 transition-colors"
            aria-label={t('breadcrumb.home', 'Home')}
          >
            <Home className="h-4 w-4" />
          </Link>
        </li>

        {crumbs.map((crumb) => (
          <li key={crumb.path} className="flex items-center gap-1">
            <ChevronRight
              className={cn('h-3.5 w-3.5 text-[#5C5C5C]/50 dark:text-[#E8D5BC]', rtlMirrorHorizontalChevron)}
              aria-hidden="true"
            />
            {crumb.isLast ? (
              <span
                className="font-medium text-[#1A1A1A] dark:text-gray-200 truncate max-w-[200px]"
                aria-current="page"
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className={cn(
                  'text-[#5C5C5C] dark:text-[#E8D5BC] hover:text-[#1A1A1A] dark:hover:text-gray-200 transition-colors truncate max-w-[150px]'
                )}
              >
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumb;
